import type { Project } from '../types/executive'

export const projects: Project[] = [
  {
    name: 'Kaiser Permanente Los Angeles Medical Center',
    location: 'Los Angeles, CA — Sunset Blvd Campus',
    sector: 'Healthcare',
    value: '$410M',
    facts: ['460 Acute-Care Beds', '1,000,000 SF Campus', 'CMAR'],
    role: 'Project Executive',
    description:
      'Ground-up replacement of Kaiser Permanente’s Los Angeles Medical Center, delivering 460 new acute-care beds and comprehensive clinical facilities across an active campus. Required sequential OSHPD/HCAI state inspections, strict California seismic compliance, and phasing that kept the adjacent hospital fully operational with no clinical downtime or utility interruptions.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/b75ff37e-ae20-48d7-8e21-ee2d9d023d0d.jpg',
  },
  {
    name: 'Providence Breeze Cancer Center',
    location: 'Providence Little Company of Mary',
    sector: 'Healthcare',
    value: '$80M',
    facts: ['108,000 SF', 'Five-Level Parking', 'CMAR'],
    role: 'Vice President / PX',
    description:
      'Outpatient facility providing advanced cancer care, including a five-level parking structure. Among the first projects awarded after Conroy became Vice President of Healthcare for Snyder Langston, where he grew the backlog from $0 to $120M.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/2ea3fa38-ceb7-4b36-af6a-27b6c50d32ef.jpg',
  },
  {
    name: 'Los Alamitos Medical Plaza',
    location: 'Los Alamitos, CA',
    sector: 'Healthcare',
    value: '$37M',
    facts: ['70,000 SF MOB', '1,077-Stall Parking', '14-Month Delivery'],
    role: 'Vice President / PX',
    description:
      'A three-story, 70,000 SF medical office building and six-level, 1,077-stall parking structure on an operating hospital campus, expanding physician office capacity. Delivered via CMAR/Design-Build on budget and on schedule in 14 months using complex logistics and phased construction.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/e22cb7b9-cc1a-4c83-8644-420d0b4c10b9.jpg',
  },
  {
    name: 'LAX APM Terminal Cores',
    location: 'Los Angeles International Airport',
    sector: 'Aviation',
    value: '$490M',
    facts: ['3 Terminal Cores', 'Zero Critical Claims', 'Design-Build'],
    role: 'Vice President, Western Region',
    description:
      'New vertical circulation and access/egress space at Tom Bradley International, Terminal 5.5, and Terminal 7, connecting passengers to the Automated People Mover. Delivered landside-only with public access open 24/7 — part of a $585M aviation portfolio completed with zero critical claims, earning the ASCE California Outstanding Airport Project award and an ENR Best Project Award of Merit.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/89b26eb8-4976-47a6-bc77-6d8c888d6d18.jpg',
  },
  {
    name: 'LAX Gateway Project',
    location: 'Los Angeles International Airport',
    sector: 'Aviation',
    value: '$120M',
    facts: ['15 Light Pylons', 'Design-Build', 'Multi-Agency'],
    role: 'Pursuit & Team Assembly Lead',
    description:
      'Design-Build reimagining of the LAX civic gateway — the Airport Traffic Control Tower, 15 illuminated pylons, and monumental signage — coordinated across LADBS, LADPW, LADOT, LABOE, Metro, Caltrans, the FAA, and the City of Los Angeles Department of Cultural Affairs.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/3f9c841f-53a7-40fe-be9a-f7ebbe274cc4.jpg',
  },
  {
    name: 'McCormick Place South Hall Expansion',
    location: 'Chicago, IL',
    sector: 'Civic',
    value: '$675M',
    facts: ['2.9 MSF', 'Joint Venture', 'Design-Build'],
    role: 'Design Management & Procurement',
    description:
      'One of the largest exhibition-hall expansions in the country, delivered by a Clark / Hunt / Morse Diesel joint venture. Responsible for design development, GMP assembly, and trade contractor procurement, with direct oversight of the concrete superstructure package. Recognized by the AIA, the Chicago Building Congress, and DBIA.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/98714492-3c6e-400c-8dbd-cc09f8f0fcd0.jpg',
  },
  {
    name: 'San Bernardino Justice Center',
    location: 'San Bernardino, CA',
    sector: 'Civic',
    value: '$339.8M',
    facts: ['383,745 SF', '11 Stories', '35 Courtrooms + 2 Hearing Rooms'],
    role: 'Public-Sector Competitive Pursuit Lead',
    description:
      'Led cross-functional estimating, scheduling, and engineering teams through a complex competitive pursuit, aligning design with budget requirements to secure the public-sector award for a 3-story linear podium and 11-story courtroom tower.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/09055abf-ebe5-433b-90a4-2fa0f46a0fc4.jpg',
  },
  {
    name: 'Chicago Board of Trade — Eastern Addition',
    location: 'Chicago, IL',
    sector: 'Commercial',
    value: '$175M',
    facts: ['350,000 SF', '45MW N+1', '1994–1997'],
    role: 'Preconstruction Manager / Project Manager',
    description:
      'Led the design-build JV team through feasibility and cost development, breaking ground within four months of initial budgeting and presenting a GMP at 50% schematic design to the Chicago Board of Trade Board of Directors. The addition delivered what was then the largest trading floor in the world, backed by 45MW of N+1 power and blast-resistant construction.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/d95c7df3-efe1-4eba-bbdd-5681424aeb21.jpg',
  },
  {
    name: 'Campus Point',
    location: 'San Diego, CA',
    sector: 'Commercial',
    value: '$80M',
    facts: ['3 Buildings', 'Life Science Conversion', 'Joint Venture'],
    role: 'Principal-in-Charge',
    description:
      'Repositioned Campus Point Center in La Jolla from “B” occupancy into a life science campus for Phase 3 Real Estate Development, including new interiors, MEP, process piping, and dynamic glazing. Led development and execution of the JV agreement and overall project oversight.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/e8b59b44-1f80-4fa0-9079-42206c66afdf.jpg',
  },
  {
    name: 'Crate and Barrel Corporate Headquarters',
    location: 'Northbrook, IL',
    sector: 'Commercial',
    value: '$60M',
    facts: ['175,000 SF', 'CMAR', 'Schedule Recovery'],
    role: 'Senior Project Manager',
    description:
      'Corporate headquarters delivered via CMAR after Conroy was brought in to recover a schedule-challenged project, working full time on site for six months and developing the recovery schedule. Completed on schedule and free of contractor claims; received an AIA Chicago Honor Award.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/44425edb-4553-43a5-9c20-77a243295025.jpg',
  },
  {
    name: 'Playa Jefferson — Building E',
    location: 'Playa Vista, CA',
    sector: 'Commercial',
    value: '$15M',
    facts: ['50,000 SF', 'GMP Contract', 'Mixed-Use'],
    role: 'Business-Unit Leadership Oversight',
    description:
      'A 50,000 SF commercial building delivered for Paul Marshall and Associates within the Playa Jefferson mixed-use development under a Guaranteed Maximum Price contract. Received an ENR California Best Project award for Southern California in the Office/Retail/Mixed-Use category.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/00092422-b3dc-4b3e-8630-f0323a941dac.jpg',
  },
]

export const projectSectors = ['All', 'Healthcare', 'Aviation', 'Civic', 'Commercial'] as const
